export interface ElementWait {
  promise: Promise<HTMLElement | null>;
  cancel: () => void;
}

export const waitForElement = (
  probe: () => HTMLElement | null,
  timeoutMs: number,
): ElementWait => {
  let settled = false;
  let observer: MutationObserver | null = null;
  let timeoutId: number | null = null;
  let frameId: number | null = null;

  const cleanup = () => {
    observer?.disconnect();
    observer = null;
    if (timeoutId !== null) window.clearTimeout(timeoutId);
    if (frameId !== null) window.cancelAnimationFrame(frameId);
    timeoutId = null;
    frameId = null;
  };

  let cancel = () => {};

  const promise = new Promise<HTMLElement | null>((resolve) => {
    const finish = (element: HTMLElement | null) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(element);
    };

    cancel = () => finish(null);

    const check = (): boolean => {
      const element = probe();
      if (!element) return false;
      frameId = window.requestAnimationFrame(() => finish(probe()));
      return true;
    };

    if (check()) return;

    observer = new MutationObserver(() => {
      check();
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        "data-tour",
        "class",
        "style",
        "hidden",
        "aria-busy",
        "aria-expanded",
        "aria-disabled",
        "disabled",
      ],
    });

    timeoutId = window.setTimeout(() => finish(null), timeoutMs);
  });

  return { promise, cancel };
};

export const watchForElement = (
  probe: () => HTMLElement | null,
  intervalMs: number,
  timeoutMs: number,
): ElementWait => {
  let settled = false;
  let intervalId: number | null = null;
  let timeoutId: number | null = null;

  const cleanup = () => {
    if (intervalId !== null) window.clearInterval(intervalId);
    if (timeoutId !== null) window.clearTimeout(timeoutId);
    intervalId = null;
    timeoutId = null;
  };

  let cancel = () => {};

  const promise = new Promise<HTMLElement | null>((resolve) => {
    const finish = (element: HTMLElement | null) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(element);
    };

    cancel = () => finish(null);

    const check = () => {
      const element = probe();
      if (element) finish(element);
    };

    check();
    if (settled) return;

    intervalId = window.setInterval(check, intervalMs);
    timeoutId = window.setTimeout(() => finish(null), timeoutMs);
  });

  return { promise, cancel };
};
